import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
    curingColour,
    curingLabel,
    useCuringEstimate,
    useCuringObservations,
    useDeleteCuringObservation,
    useRefreshCuring,
} from '../../api/curing';
import type { CuringSeriesPoint } from '../../types/curing';
import CuringObservationDialog from './CuringObservationDialog';

interface CuringTabProps {
    projectId: string;
    /** Project centre, used as the starting point for a new observation. */
    centreLat?: number | null;
    centreLon?: number | null;
}

/** Simple SVG sparkline of the season's curing so far. */
function CuringSparkline({ series }: { series: CuringSeriesPoint[] }) {
    const points = useMemo(
        () => series.filter((p) => p.mean !== null) as Required<CuringSeriesPoint>[],
        [series],
    );
    if (points.length < 2) return null;

    const width = 520;
    const height = 90;
    const pad = 4;
    const xs = points.map((_, i) => pad + (i * (width - 2 * pad)) / (points.length - 1));
    const ys = points.map((p) => height - pad - ((p.mean as number) / 100) * (height - 2 * pad));
    const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Curing so far this season
            </Typography>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
                {[25, 50, 75].map((pct) => {
                    const y = height - pad - (pct / 100) * (height - 2 * pad);
                    return (
                        <line
                            key={pct}
                            x1={pad} x2={width - pad} y1={y} y2={y}
                            stroke="rgba(255,255,255,0.08)" strokeWidth={1}
                        />
                    );
                })}
                <path d={path} fill="none" stroke="#7cb342" strokeWidth={2} />
                {xs.map((x, i) => (
                    <circle key={i} cx={x} cy={ys[i]} r={2.5} fill={curingColour(points[i].mean)} />
                ))}
            </svg>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {points[0].date}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {points[points.length - 1].date}
                </Typography>
            </Box>
        </Box>
    );
}

function Stat({ label, value, colour, caption }: {
    label: string; value: string; colour?: string; caption?: string;
}) {
    return (
        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.04)', flex: '1 1 140px', minWidth: 140 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ color: colour ?? '#fff', fontWeight: 600 }}>
                {value}
            </Typography>
            {caption && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 0.5 }}>
                    {caption}
                </Typography>
            )}
        </Paper>
    );
}

/**
 * Grass curing for a project: what the satellite sees, how confident it is,
 * and the ground observations that correct it.
 */
const CuringTab: React.FC<CuringTabProps> = ({ projectId, centreLat, centreLon }) => {
    const { data, isLoading } = useCuringEstimate(projectId);
    const { data: observations } = useCuringObservations(projectId);
    const refresh = useRefreshCuring(projectId);
    const deleteObservation = useDeleteCuringObservation(projectId);
    const [refreshError, setRefreshError] = useState<string | null>(null);
    const [addingObservation, setAddingObservation] = useState(false);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const estimate = data?.available ? data.estimate : undefined;
    const summary = estimate?.summary;
    const state = data?.state;

    const handleRefresh = async () => {
        setRefreshError(null);
        try {
            await refresh.mutateAsync();
        } catch {
            setRefreshError('A refresh is already under way. It should finish shortly.');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        Grass curing
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 620 }}>
                        How much of the grass across this project has dried off and is
                        available to burn. Measured from satellite imagery each week, and
                        used automatically by simulations run within a fortnight of a
                        reading. Under pine, where the satellite only sees the canopy, the
                        understorey is modelled from rain and evaporation instead.
                    </Typography>
                </Box>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refresh.isPending || Boolean(state?.queued)}
                    variant="outlined"
                    size="small"
                >
                    {state?.queued ? 'Refreshing…' : 'Refresh now'}
                </Button>
            </Box>

            {refreshError && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRefreshError(null)}>
                    {refreshError}
                </Alert>
            )}

            {state?.last_error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The last attempt to update the curing estimate did not finish. It will
                    be retried automatically.
                </Alert>
            )}

            {!estimate && (
                <Alert severity="info">
                    No curing estimate yet for this project. One is produced automatically
                    within a few minutes of the project being set up, and refreshed weekly.
                </Alert>
            )}

            {estimate && summary && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Stat
                            label="Average across grass"
                            value={summary.mean === null ? '—' : `${Math.round(summary.mean)}%`}
                            colour={curingColour(summary.mean)}
                        />
                        <Stat
                            label="Range (10th–90th)"
                            value={
                                summary.p10 === null || summary.p90 === null
                                    ? '—'
                                    : `${Math.round(summary.p10)}–${Math.round(summary.p90)}%`
                            }
                        />
                        <Stat
                            label="At least half cured"
                            value={summary.pct_ge_50 === null ? '—' : `${Math.round(summary.pct_ge_50)}%`}
                        />
                        <Stat
                            label="Grass area"
                            value={`${Math.round(summary.grass_area_ha).toLocaleString()} ha`}
                        />
                        {summary.pine && (
                            <Stat
                                label={`Pine understorey (${Math.round(summary.pine.area_ha).toLocaleString()} ha)`}
                                value={summary.pine.curing === null ? '—' : `${Math.round(summary.pine.curing)}%`}
                                colour={curingColour(summary.pine.curing)}
                                caption={
                                    summary.pine.source !== 'weather_model'
                                        ? 'Weather data unavailable; no value until the next refresh.'
                                        : summary.pine.observations > 0
                                            ? `Modelled from soil moisture; level set by ${summary.pine.observations} observation${summary.pine.observations === 1 ? '' : 's'} in the pine.`
                                            : 'Modelled from soil moisture. Not yet checked on the ground — add an observation in the pine to set the level.'
                                }
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                            size="small"
                            label={`${curingLabel(summary.mean)} on ${estimate.estimate_date}`}
                            sx={{ bgcolor: curingColour(summary.mean), color: '#000' }}
                        />
                        {summary.scene_count !== undefined && (
                            <Tooltip title="Satellite passes that contributed to this reading">
                                <Chip size="small" variant="outlined" label={`${summary.scene_count} satellite passes`} />
                            </Tooltip>
                        )}
                        {summary.valid_fraction !== undefined && (
                            <Tooltip title="Share of the project the satellite could see clearly">
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${Math.round(summary.valid_fraction * 100)}% clear view`}
                                />
                            </Tooltip>
                        )}
                    </Box>

                    {estimate.series && <CuringSparkline series={estimate.series} />}
                </>
            )}

            <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
                        Ground observations
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setAddingObservation(true)}>
                        Record an observation
                    </Button>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                    What people have actually seen in the paddock. Each one corrects the
                    satellite reading nearby, and lets us show how closely the two agree.
                </Typography>

                {(observations?.length ?? 0) === 0 ? (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        None recorded yet.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Observed</TableCell>
                                <TableCell>Satellite said</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Recorded by</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(observations ?? []).map((obs) => (
                                <TableRow key={obs.id}>
                                    <TableCell>{obs.observed_on}</TableCell>
                                    <TableCell>{obs.curing_pct}%</TableCell>
                                    <TableCell>
                                        {obs.model_value_at_obs === null
                                            ? '—'
                                            : `${Math.round(obs.model_value_at_obs)}%`}
                                    </TableCell>
                                    <TableCell>{obs.method.replace('_', ' ')}</TableCell>
                                    <TableCell>{obs.created_by_name ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteObservation.mutate(obs.id)}
                                            aria-label="Delete observation"
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <CuringObservationDialog
                open={addingObservation}
                onClose={() => setAddingObservation(false)}
                projectId={projectId}
                defaultLat={centreLat}
                defaultLon={centreLon}
            />
        </Box>
    );
};

export default CuringTab;
